/**
 * PricingTab — Per-cell optimistic editor for Price Tiers and Variants
 *
 * Features:
 * - Per-cell onBlur PATCH with yellow→green/red feedback
 * - Add single tier via POST, delete via DELETE
 * - Range overlap validation (red highlight, client-side)
 * - Variant bulk-save (unchanged)
 */
import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, Plus, Trash2, Save, DollarSign,
  Package, AlertCircle, AlertTriangle,
} from 'lucide-react';
import { useHaptic } from '@/hooks/useHaptic';
import {
  AdminCatalogSystem,
  patchPriceTier,
  createPriceTier,
  deletePriceTier,
  invalidateCacheTag,
} from '@/api';

interface PriceTier {
  id?: number;
  tier_label: string;
  tier_type: string;
  width_min_cm: number | null;
  width_max_cm: number | null;
  height_min_cm: number | null;
  height_max_cm: number | null;
  base_price: number;
  price_per_cm_width: number | null;
  price_per_cm_height: number | null;
  is_active: boolean;
}

interface SystemVariant {
  id?: number;
  variant_code: string;
  sku: string;
  motor_id: number | null;
  width_min_m: number | null;
  width_max_m: number | null;
  height_min_m: number | null;
  height_max_m: number | null;
  price_adjustment: number;
  is_default: boolean;
  is_active: boolean;
}

type CellState = 'idle' | 'saving' | 'saved' | 'error';

interface PricingTabProps {
  system: AdminCatalogSystem;
  priceTiers: PriceTier[];
  variants: SystemVariant[];
  onSavePriceTiers: (tiers: PriceTier[]) => Promise<void>;
  onSaveVariants: (variants: SystemVariant[]) => Promise<void>;
  isSaving: boolean;
}

function hasWidthOverlap(tiers: PriceTier[]): Set<number> {
  const overlapping = new Set<number>();
  const active = tiers
    .map((t, i) => ({ ...t, _idx: i }))
    .filter((t) => t.is_active && t.width_min_cm != null && t.width_max_cm != null);
  for (let i = 0; i < active.length; i++) {
    for (let j = i + 1; j < active.length; j++) {
      const a = active[i], b = active[j];
      if (a.width_min_cm! < b.width_max_cm! && b.width_min_cm! < a.width_max_cm!) {
        overlapping.add(a._idx);
        overlapping.add(b._idx);
      }
    }
  }
  return overlapping;
}

export function PricingTab({
  system,
  priceTiers,
  variants,
  onSavePriceTiers: _onSavePriceTiers,
  onSaveVariants,
  isSaving,
}: PricingTabProps) {
  const haptic = useHaptic();
  const [openAccordion, setOpenAccordion] = useState<'prices' | 'variants' | null>('prices');

  const [tiers, setTiers] = useState<PriceTier[]>(priceTiers);
  const [editingVariants, setEditingVariants] = useState<SystemVariant[]>(variants);
  const [cellStates, setCellStates] = useState<Record<string, CellState>>({});
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const overlappingIdxs = hasWidthOverlap(tiers);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const cellKey = (tierId: number | undefined, idx: number, field: string) =>
    `${tierId ?? `new-${idx}`}-${field}`;

  const flashCell = useCallback((key: string, state: CellState) => {
    setCellStates((s) => ({ ...s, [key]: state }));
    if (state === 'saved' || state === 'error') {
      setTimeout(() => setCellStates((s) => ({ ...s, [key]: 'idle' })), 1500);
    }
  }, []);

  const cellBorder = (key: string, hasOverlap = false) => {
    if (hasOverlap) return 'border-red-400/60';
    const s = cellStates[key];
    if (s === 'saving') return 'border-yellow-400/60 animate-pulse';
    if (s === 'saved') return 'border-emerald-400/60';
    if (s === 'error') return 'border-red-400/60';
    return 'border-white/10';
  };

  const handleCellBlur = async (
    tier: PriceTier,
    idx: number,
    field: keyof PriceTier,
    value: unknown
  ) => {
    if (!tier.id) return; // unsaved new tier, skip
    const key = cellKey(tier.id, idx, field);
    flashCell(key, 'saving');
    try {
      await patchPriceTier(system.id, tier.id, { [field]: value });
      flashCell(key, 'saved');
      await invalidateCacheTag(`systems:${system.slug}`).catch(() => null);
    } catch {
      flashCell(key, 'error');
      haptic.error();
    }
  };

  const updateTier = (idx: number, field: keyof PriceTier, value: unknown) => {
    setTiers((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handleAddTier = async () => {
    haptic.selection();
    const draft: PriceTier = {
      tier_label: 'Новый диапазон',
      tier_type: 'range',
      width_min_cm: null,
      width_max_cm: null,
      height_min_cm: null,
      height_max_cm: null,
      base_price: 0,
      price_per_cm_width: null,
      price_per_cm_height: null,
      is_active: true,
    };
    try {
      const res = await createPriceTier(system.id, draft as unknown as Record<string, unknown>) as unknown as PriceTier;
      setTiers((prev) => [...prev, { ...draft, id: res.id }]);
      showToast('Диапазон добавлен');
    } catch {
      setError('Ошибка создания диапазона');
      haptic.error();
    }
  };

  const handleDeleteTier = async (idx: number) => {
    haptic.selection();
    const tier = tiers[idx];
    if (tier.id) {
      try {
        await deletePriceTier(system.id, tier.id);
        await invalidateCacheTag(`systems:${system.slug}`).catch(() => null);
      } catch {
        setError('Ошибка удаления диапазона');
        haptic.error();
        return;
      }
    }
    setTiers((prev) => prev.filter((_, i) => i !== idx));
  };

  // Variants (bulk save unchanged)
  const addVariant = () => {
    haptic.selection();
    const newVariant: SystemVariant = {
      variant_code: 'Новый вариант',
      sku: `${system.slug}-variant-${editingVariants.length + 1}`,
      motor_id: null,
      width_min_m: null,
      width_max_m: null,
      height_min_m: null,
      height_max_m: null,
      price_adjustment: 0,
      is_default: editingVariants.length === 0,
      is_active: true,
    };
    setEditingVariants([...editingVariants, newVariant]);
  };

  const updateVariant = (idx: number, field: keyof SystemVariant, value: unknown) => {
    const updated = [...editingVariants];
    updated[idx] = { ...updated[idx], [field]: value };
    setEditingVariants(updated);
  };

  const removeVariant = (idx: number) => {
    haptic.selection();
    setEditingVariants(editingVariants.filter((_, i) => i !== idx));
  };

  const saveVariants = async () => {
    try {
      await onSaveVariants(editingVariants);
      haptic.success();
      setError(null);
    } catch {
      setError('Ошибка сохранения вариантов');
      haptic.error();
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 text-xs bg-emerald-500/90 text-white rounded-lg shadow-lg"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error banner */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex items-center gap-2 px-4 py-2 text-xs bg-orange-500/10 text-orange-400 rounded-lg"
          >
            <AlertCircle size={14} />
            {error}
            <button onClick={() => setError(null)} className="ml-auto hover:text-orange-300">×</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overlap warning */}
      <AnimatePresence>
        {overlappingIdxs.size > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2 px-4 py-2 text-xs bg-red-500/10 text-red-400 rounded-lg"
          >
            <AlertTriangle size={14} />
            Обнаружено пересечение диапазонов ширины. Исправьте, иначе цена может рассчитаться неверно.
          </motion.div>
        )}
      </AnimatePresence>

      {/* Accordion 1: Price Tiers */}
      <div className="rounded-xl overflow-hidden border border-white/10 bg-white/[0.02]">
        <button
          onClick={() => setOpenAccordion(openAccordion === 'prices' ? null : 'prices')}
          className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <DollarSign size={18} className="text-emerald-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-white/90">Ценообразование</h3>
              <p className="text-xs text-white/50">{tiers.length} диапазонов цен</p>
            </div>
          </div>
          <ChevronDown
            size={20}
            className={`text-white/40 transition-transform ${openAccordion === 'prices' ? 'rotate-180' : ''}`}
          />
        </button>

        <AnimatePresence>
          {openAccordion === 'prices' && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              className="overflow-hidden"
            >
              <div className="p-4 pt-0 space-y-3">
                {tiers.length === 0 ? (
                  <div className="text-center py-6 text-sm text-white/40">
                    Нет диапазонов цен. Добавьте первый.
                  </div>
                ) : (
                  tiers.map((tier, idx) => {
                    const hasOverlap = overlappingIdxs.has(idx);
                    return (
                      <div
                        key={tier.id ?? `new-${idx}`}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                          hasOverlap
                            ? 'bg-red-500/5 border-red-400/30'
                            : 'bg-white/[0.03] border-white/5'
                        }`}
                      >
                        {hasOverlap && <AlertTriangle size={14} className="text-red-400 shrink-0" />}
                        <div className="flex-1 grid grid-cols-4 gap-2">
                          <input
                            type="text"
                            value={tier.tier_label}
                            onChange={(e) => updateTier(idx, 'tier_label', e.target.value)}
                            onBlur={(e) => handleCellBlur(tier, idx, 'tier_label', e.target.value)}
                            className={`px-2 py-1.5 rounded-lg bg-white/5 border text-sm text-white placeholder-white/30 focus:outline-none transition-colors ${cellBorder(cellKey(tier.id, idx, 'tier_label'))}`}
                            placeholder="Название"
                          />
                          <div className="flex gap-1">
                            <input
                              type="number"
                              value={tier.width_min_cm ?? ''}
                              onChange={(e) => updateTier(idx, 'width_min_cm', parseFloat(e.target.value) || null)}
                              onBlur={(e) => handleCellBlur(tier, idx, 'width_min_cm', parseFloat(e.target.value) || null)}
                              className={`w-full px-2 py-1.5 rounded-lg bg-white/5 border text-sm text-white placeholder-white/30 focus:outline-none transition-colors ${cellBorder(cellKey(tier.id, idx, 'width_min_cm'), hasOverlap)}`}
                              placeholder="от"
                            />
                            <input
                              type="number"
                              value={tier.width_max_cm ?? ''}
                              onChange={(e) => updateTier(idx, 'width_max_cm', parseFloat(e.target.value) || null)}
                              onBlur={(e) => handleCellBlur(tier, idx, 'width_max_cm', parseFloat(e.target.value) || null)}
                              className={`w-full px-2 py-1.5 rounded-lg bg-white/5 border text-sm text-white placeholder-white/30 focus:outline-none transition-colors ${cellBorder(cellKey(tier.id, idx, 'width_max_cm'), hasOverlap)}`}
                              placeholder="до"
                            />
                          </div>
                          <input
                            type="number"
                            value={tier.base_price || ''}
                            onChange={(e) => updateTier(idx, 'base_price', parseFloat(e.target.value) || 0)}
                            onBlur={(e) => handleCellBlur(tier, idx, 'base_price', parseFloat(e.target.value) || 0)}
                            className={`px-2 py-1.5 rounded-lg bg-white/5 border text-sm text-white placeholder-white/30 focus:outline-none transition-colors ${cellBorder(cellKey(tier.id, idx, 'base_price'))}`}
                            placeholder="Цена ₽"
                          />
                          <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={tier.is_active}
                              onChange={(e) => {
                                updateTier(idx, 'is_active', e.target.checked);
                                handleCellBlur(tier, idx, 'is_active', e.target.checked);
                              }}
                              className="rounded border-white/20 bg-white/5 text-emerald-500 focus:ring-emerald-500/20"
                            />
                            Активен
                          </label>
                        </div>
                        <button
                          onClick={() => handleDeleteTier(idx)}
                          className="p-2 rounded-lg hover:bg-red-500/10 text-white/40 hover:text-red-400 transition-colors shrink-0"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    );
                  })
                )}

                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={handleAddTier}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors text-sm"
                  >
                    <Plus size={16} />
                    Добавить диапазон
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Accordion 2: Variants */}
      <div className="rounded-xl overflow-hidden border border-white/10 bg-white/[0.02]">
        <button
          onClick={() => setOpenAccordion(openAccordion === 'variants' ? null : 'variants')}
          className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Package size={18} className="text-blue-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-white/90">Варианты комплектации</h3>
              <p className="text-xs text-white/50">{editingVariants.length} вариантов</p>
            </div>
          </div>
          <ChevronDown
            size={20}
            className={`text-white/40 transition-transform ${openAccordion === 'variants' ? 'rotate-180' : ''}`}
          />
        </button>

        <AnimatePresence>
          {openAccordion === 'variants' && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              className="overflow-hidden"
            >
              <div className="p-4 pt-0 space-y-3">
                {editingVariants.length === 0 ? (
                  <div className="text-center py-6 text-sm text-white/40">
                    Нет вариантов. Добавьте первый.
                  </div>
                ) : (
                  editingVariants.map((variant, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/5"
                    >
                      <div className="flex-1 grid grid-cols-5 gap-3">
                        <input
                          type="text"
                          value={variant.variant_code}
                          onChange={(e) => updateVariant(idx, 'variant_code', e.target.value)}
                          className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50"
                          placeholder="Код варианта"
                        />
                        <input
                          type="text"
                          value={variant.sku}
                          onChange={(e) => updateVariant(idx, 'sku', e.target.value)}
                          className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50"
                          placeholder="SKU"
                        />
                        <input
                          type="number"
                          value={variant.price_adjustment || ''}
                          onChange={(e) => updateVariant(idx, 'price_adjustment', parseFloat(e.target.value) || 0)}
                          className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-white/30 focus:outline-none focus:border-blue-500/50"
                          placeholder="Доплата ₽"
                        />
                        <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={variant.is_default}
                            onChange={(e) => updateVariant(idx, 'is_default', e.target.checked)}
                            className="rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500/20"
                          />
                          По умолчанию
                        </label>
                        <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={variant.is_active}
                            onChange={(e) => updateVariant(idx, 'is_active', e.target.checked)}
                            className="rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500/20"
                          />
                          Активен
                        </label>
                      </div>
                      <button
                        onClick={() => removeVariant(idx)}
                        className="p-2 rounded-lg hover:bg-red-500/10 text-white/40 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))
                )}

                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={addVariant}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors text-sm"
                  >
                    <Plus size={16} />
                    Добавить вариант
                  </button>

                  {JSON.stringify(editingVariants) !== JSON.stringify(variants) && (
                    <button
                      onClick={saveVariants}
                      disabled={isSaving}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors text-sm disabled:opacity-50"
                    >
                      <Save size={16} />
                      {isSaving ? 'Сохранение...' : 'Сохранить варианты'}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
